/**
 * TechnicianTaskDetail Page
 *
 * Complete task detail view for technicians.
 * Includes status changes, time tracking, comments, photos, and material requests.
 *
 * @module pages/technician/TechnicianTaskDetail
 */

import React, { useState } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Badge,
  Form,
  Spinner,
  Alert,
  Modal,
  Tab,
  Tabs,
  Dropdown,
} from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  getTask,
  updateTaskStatus,
  addTaskComment,
  getTaskComments,
  updateTaskComment,
  deleteTaskComment,
} from '../../services/taskService';
import TaskAssigneeAvatar from '../../components/tasks/shared/TaskAssigneeAvatar';
import KebabMenu from '../../components/common/KebabMenu';
import {
  checkIn,
  checkOut,
  getActiveEntry,
} from '../../services/timeEntryService';
import { uploadTaskPhotos } from '../../services/progressService';
import {
  getTaskChecklistItems,
  addTaskChecklistItem,
  toggleTaskChecklistItem,
  deleteTaskChecklistItem,
} from '../../services/taskChecklistService';
import { TASK_STATUS, TASK_PRIORITY, esProyectoPersonal } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-toastify';
import './TechnicianPages.css';
import './TechnicianTaskDetail.css';

const TechnicianTaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Refs
  const cameraInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);

  // Local state
  const [activeTab, setActiveTab] = useState('details');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [commentText, setCommentText] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch task details
  const {
    data: taskResponse,
    isLoading: taskLoading,
    error: taskError,
  } = useQuery({
    queryKey: ['task', id],
    queryFn: () => getTask(id),
    enabled: !!id,
  });

  // Fetch comments
  const { data: commentsResponse, isLoading: commentsLoading } = useQuery({
    queryKey: ['task-comments', id],
    queryFn: () => getTaskComments(id),
    enabled: !!id && activeTab === 'comments',
  });

  // Fetch checklist items
  const {
    data: checklistResponse,
    isLoading: checklistItemsLoading,
    refetch: refetchChecklist,
  } = useQuery({
    queryKey: ['task-checklist', id],
    queryFn: () => getTaskChecklistItems(id),
    enabled: !!id && activeTab === 'checklist',
  });

  // Fetch active time entry
  const { data: activeEntryResponse, refetch: refetchActiveEntry } = useQuery({
    queryKey: ['active-time-entry'],
    queryFn: getActiveEntry,
  });

  const task = taskResponse?.data;
  const comments = commentsResponse?.data || [];
  const checklistItems = checklistResponse?.data || [];
  const activeEntry = activeEntryResponse?.data;
  const isTimerRunning = activeEntry?.task_id === id && !activeEntry?.check_out_time;

  // Get elapsed time string (no real-time ticker)
  const getElapsedString = (startIso) => {
    if (!startIso) return '';
    const diffMs = Date.now() - new Date(startIso).getTime();
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  };

  const formatCheckInTime = (isoString) => {
    if (!isoString) return '--:--';
    return new Date(isoString).toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Status mutation
  const statusMutation = useMutation({
    mutationFn: (newStatus) => updateTaskStatus(id, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] });
      queryClient.invalidateQueries({ queryKey: ['technician-tasks'] });
      setSuccess('Estado actualizado correctamente');
      setShowStatusModal(false);
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      setError(err.message || 'Error al actualizar estado');
    },
  });

  // Check-in mutation (GPS captured inside service)
  const checkInMutation = useMutation({
    mutationFn: () => checkIn({
      project_id: task?.project?.id,
      task_id: id,
    }),
    onSuccess: (result) => {
      if (result.error) {
        setError(result.error.message || 'Error al iniciar jornada');
        return;
      }
      refetchActiveEntry();
      setSuccess('Jornada iniciada');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      setError(err.message || 'Error al iniciar jornada');
    },
  });

  // Check-out mutation (GPS captured inside service)
  const checkOutMutation = useMutation({
    mutationFn: () => checkOut({ entry_id: activeEntry?.id }),
    onSuccess: (result) => {
      if (result.error) {
        setError(result.error.message || 'Error al finalizar jornada');
        return;
      }
      refetchActiveEntry();
      const hours = result.data?.total_hours ? `${result.data.total_hours}h` : '';
      setSuccess(`Jornada finalizada. ${hours}`);
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (err) => {
      setError(err.message || 'Error al finalizar jornada');
    },
  });

  // Comment mutation
  const commentMutation = useMutation({
    mutationFn: () => addTaskComment(id, commentText, user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', id] });
      setCommentText('');
      setShowCommentModal(false);
      setSuccess('Comentario agregado');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      setError(err.message || 'Error al agregar comentario');
    },
  });

  // Edit comment mutation
  const editCommentMutation = useMutation({
    mutationFn: ({ commentId, content }) => updateTaskComment(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', id] });
      setEditingCommentId(null);
      setEditCommentText('');
      toast.success('Comentario actualizado');
    },
    onError: (err) => {
      setError(err.message || 'Error al editar comentario');
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId) => deleteTaskComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-comments', id] });
      setDeleteCommentId(null);
      toast.success('Comentario eliminado');
    },
    onError: (err) => {
      setError(err.message || 'Error al eliminar comentario');
    },
  });

  // Handlers
  const handleCheckIn = () => {
    if (!isTimerRunning) {
      checkInMutation.mutate();
    }
  };

  const handleCheckOut = () => {
    if (isTimerRunning && activeEntry) {
      checkOutMutation.mutate();
    }
  };

  const handleStatusChange = (newStatus) => {
    statusMutation.mutate(newStatus);
  };

  const handleAddComment = () => {
    if (commentText.trim()) {
      commentMutation.mutate();
    }
  };

  // Photo handlers
  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imagenes');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10MB');
      return;
    }

    setSelectedFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile || !task?.project_id) return;

    setUploadingPhoto(true);
    try {
      // Try to get GPS coordinates
      let gpsCoords = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        gpsCoords = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
      } catch {
        // GPS not available, continue without it
      }

      const { errors } = await uploadTaskPhotos(
        id,
        task.project_id,
        [selectedFile],
        photoCaption || null,
        gpsCoords
      );

      if (errors?.length > 0) {
        throw errors[0];
      }

      toast.success('Foto subida exitosamente');
      setShowPhotoModal(false);
      setSelectedFile(null);
      setPhotoPreview(null);
      setPhotoCaption('');
      // Reset file inputs
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    } catch (err) {
      console.error('Upload photo error:', err);
      toast.error(err.message || 'No se pudo subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleClosePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedFile(null);
    setPhotoPreview(null);
    setPhotoCaption('');
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  // Checklist handlers
  const handleToggleChecklistItem = async (itemId, currentCompleted) => {
    try {
      setChecklistLoading(true);
      const { error: toggleError } = await toggleTaskChecklistItem(itemId, !currentCompleted);
      if (toggleError) throw toggleError;
      refetchChecklist();
    } catch (err) {
      toast.error(err.message || 'Error al actualizar item');
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    try {
      setChecklistLoading(true);
      const { error: addError } = await addTaskChecklistItem(id, newChecklistItem.trim());
      if (addError) throw addError;
      setNewChecklistItem('');
      refetchChecklist();
      toast.success('Item agregado');
    } catch (err) {
      toast.error(err.message || 'Error al agregar item');
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      setChecklistLoading(true);
      const { error: delError } = await deleteTaskChecklistItem(itemId);
      if (delError) throw delError;
      refetchChecklist();
    } catch (err) {
      toast.error(err.message || 'Error al eliminar item');
    } finally {
      setChecklistLoading(false);
    }
  };

  // Priority config
  const getPriorityConfig = (priority) => {
    const config = {
      [TASK_PRIORITY.URGENT]: { bg: 'danger', text: 'Urgente' },
      [TASK_PRIORITY.HIGH]: { bg: 'warning', text: 'Alta' },
      [TASK_PRIORITY.MEDIUM]: { bg: 'info', text: 'Media' },
      [TASK_PRIORITY.LOW]: { bg: 'secondary', text: 'Baja' },
    };
    return config[priority] || config[TASK_PRIORITY.MEDIUM];
  };

  // Status config
  const getStatusConfig = (status) => {
    const config = {
      [TASK_STATUS.PENDING]: { bg: 'secondary', text: 'Pendiente', icon: 'bi-hourglass-split' },
      [TASK_STATUS.IN_PROGRESS]: { bg: 'primary', text: 'En Progreso', icon: 'bi-gear-fill' },
      [TASK_STATUS.COMPLETED]: { bg: 'success', text: 'Completada', icon: 'bi-check-circle-fill' },
      [TASK_STATUS.BLOCKED]: { bg: 'danger', text: 'Bloqueada', icon: 'bi-x-octagon-fill' },
      [TASK_STATUS.REVIEW]: { bg: 'warning', text: 'En Revision', icon: 'bi-eye-fill' },
    };
    return config[status] || config[TASK_STATUS.PENDING];
  };

  // Available status transitions
  const getAvailableStatuses = (currentStatus) => {
    const transitions = {
      [TASK_STATUS.PENDING]: [TASK_STATUS.IN_PROGRESS, TASK_STATUS.BLOCKED],
      [TASK_STATUS.IN_PROGRESS]: [TASK_STATUS.COMPLETED, TASK_STATUS.BLOCKED, TASK_STATUS.REVIEW],
      [TASK_STATUS.BLOCKED]: [TASK_STATUS.PENDING, TASK_STATUS.IN_PROGRESS],
      [TASK_STATUS.REVIEW]: [TASK_STATUS.IN_PROGRESS, TASK_STATUS.COMPLETED],
      [TASK_STATUS.COMPLETED]: [TASK_STATUS.IN_PROGRESS],
    };
    return transitions[currentStatus] || [];
  };

  if (taskLoading) {
    return (
      <Container fluid className="technician-page d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (taskError || !task) {
    return (
      <Container fluid className="technician-page">
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error al cargar la tarea. <Link to="/tecnico/tareas">Volver a tareas</Link>
        </Alert>
      </Container>
    );
  }

  const priorityConfig = getPriorityConfig(task.priority);
  const statusConfig = getStatusConfig(task.status);
  const availableStatuses = getAvailableStatuses(task.status);

  return (
    <Container fluid className="technician-page task-detail-page">
      {/* Header */}
      <div className="page-header">
        <Link to="/tecnico/tareas" className="back-link">
          <i className="bi bi-arrow-left"></i> Volver
        </Link>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <span className="task-number">{task.task_number || `#${task.id.slice(0, 8)}`}</span>
            <h1 className="page-title mb-1">{task.title}</h1>
          </div>
          <Badge bg={statusConfig.bg} className="status-badge">
            <i className={`bi ${statusConfig.icon} me-1`}></i>
            {statusConfig.text}
          </Badge>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Jornada Card */}
      <Card className="timer-card mb-3">
        <Card.Body>
          {!isTimerRunning ? (
            <>
              <div className="timer-display">
                <div className="timer-label" style={{ fontSize: '1rem', opacity: 1 }}>
                  <i className="bi bi-clock me-2"></i>Sin jornada activa
                </div>
              </div>
              <div className="timer-actions">
                <Button
                  variant="success"
                  className="timer-btn"
                  onClick={handleCheckIn}
                  disabled={checkInMutation.isPending}
                >
                  {checkInMutation.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="bi bi-play-fill me-2"></i>
                      Iniciar Jornada
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="timer-display">
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>
                  Inicio: {formatCheckInTime(activeEntry.check_in_time)}
                </div>
                <div className="timer-value">
                  {getElapsedString(activeEntry.check_in_time)}
                </div>
                <div className="timer-label">Tiempo trabajando</div>
              </div>
              <div className="timer-actions">
                <Button
                  variant="danger"
                  className="timer-btn"
                  onClick={handleCheckOut}
                  disabled={checkOutMutation.isPending}
                >
                  {checkOutMutation.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="bi bi-stop-fill me-2"></i>
                      Finalizar Jornada
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Quick Actions */}
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-3">Acciones Rapidas</h6>
          <Row className="g-2">
            <Col xs={6}>
              <Button
                variant="outline-primary"
                className="w-100 action-btn"
                onClick={() => setShowStatusModal(true)}
              >
                <i className="bi bi-arrow-repeat me-2"></i>
                Cambiar Estado
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                variant="outline-primary"
                className="w-100 action-btn"
                onClick={() => setShowCommentModal(true)}
              >
                <i className="bi bi-chat-text me-2"></i>
                Comentar
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                variant="outline-primary"
                className="w-100 action-btn"
                onClick={() => setShowPhotoModal(true)}
              >
                <i className="bi bi-camera me-2"></i>
                Subir Foto
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                variant="outline-primary"
                className="w-100 action-btn"
                onClick={() => navigate(`/tecnico/materiales/solicitar?task=${id}`)}
              >
                <i className="bi bi-box-seam me-2"></i>
                Solicitar Material
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="task-tabs mb-3"
      >
        <Tab eventKey="details" title="Detalles">
          <Card>
            <Card.Body>
              {/* Description */}
              {task.description && (
                <div className="detail-section">
                  <h6 className="detail-label">Descripcion</h6>
                  <p className="detail-value">{task.description}</p>
                </div>
              )}

              {/* Project — #791: el contenedor personal no linkea a
                  /tecnico/proyectos (no aparece en esa lista, #405) ni
                  muestra su título crudo. */}
              {task.project && (
                <div className="detail-section">
                  <h6 className="detail-label">Proyecto</h6>
                  <div className="detail-value">
                    {esProyectoPersonal(task.project.project_number) ? (
                      <span><i className="bi bi-person-check me-1"></i>Mi lista personal</span>
                    ) : (
                      <Link to={`/tecnico/proyectos/${task.project.id}`}>
                        {task.project.title || task.project.project_number}
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Client — #791: vínculo directo de la tarea (prospección). */}
              {task.client?.company_name && (
                <div className="detail-section">
                  <h6 className="detail-label">Cliente</h6>
                  <div className="detail-value">
                    <i className="bi bi-building me-1"></i>{task.client.company_name}
                  </div>
                </div>
              )}

              {/* Priority */}
              <div className="detail-section">
                <h6 className="detail-label">Prioridad</h6>
                <Badge bg={priorityConfig.bg}>{priorityConfig.text}</Badge>
              </div>

              {/* Dates */}
              <Row>
                {task.start_date && (
                  <Col xs={6}>
                    <div className="detail-section">
                      <h6 className="detail-label">Inicio</h6>
                      <p className="detail-value">
                        {formatDate(task.start_date)}
                      </p>
                    </div>
                  </Col>
                )}
                {task.due_date && (
                  <Col xs={6}>
                    <div className="detail-section">
                      <h6 className="detail-label">Vencimiento</h6>
                      <p className="detail-value">
                        {formatDate(task.due_date)}
                      </p>
                    </div>
                  </Col>
                )}
              </Row>

              {/* Hours */}
              <Row>
                {task.estimated_hours && (
                  <Col xs={6}>
                    <div className="detail-section">
                      <h6 className="detail-label">Horas Estimadas</h6>
                      <p className="detail-value">{task.estimated_hours}h</p>
                    </div>
                  </Col>
                )}
                {task.actual_hours && (
                  <Col xs={6}>
                    <div className="detail-section">
                      <h6 className="detail-label">Horas Reales</h6>
                      <p className="detail-value">{task.actual_hours}h</p>
                    </div>
                  </Col>
                )}
              </Row>

              {/* Assignees */}
              {task.assignees && task.assignees.length > 0 && (
                <div className="detail-section">
                  <h6 className="detail-label">Asignados</h6>
                  <div className="assignees-list d-flex flex-wrap gap-2">
                    {task.assignees.map((assignment) => (
                      <div key={assignment.id} className="assignee-item d-flex align-items-center gap-2">
                        <TaskAssigneeAvatar
                          assignee={assignment.assigned_user ? { id: assignment.assigned_user.id, full_name: assignment.assigned_user.full_name, avatar_url: assignment.assigned_user.avatar_url } : null}
                          size="sm"
                        />
                        <span>{assignment.assigned_user?.full_name || 'Usuario'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="comments" title={`Comentarios (${comments.length})`}>
          <Card>
            <Card.Body>
              {commentsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-chat-dots display-4 mb-3 d-block"></i>
                  <p>No hay comentarios</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowCommentModal(true)}
                  >
                    Agregar comentario
                  </Button>
                </div>
              ) : (
                <div className="comments-list">
                  {comments.map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-header">
                        <div className="d-flex align-items-center gap-2">
                          <TaskAssigneeAvatar
                            assignee={comment.user ? { id: comment.user.id, full_name: comment.user.full_name, avatar_url: comment.user.avatar_url } : null}
                            size="sm"
                          />
                          <span className="comment-author">
                            {comment.user?.full_name || 'Usuario'}
                          </span>
                          <span className="comment-date">
                            {new Date(comment.created_at).toLocaleDateString('es-CR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {comment.edited && <small className="text-muted ms-1">(editado)</small>}
                          </span>
                        </div>
                        {(user?.id === comment.created_by) && (
                          <KebabMenu
                            label={`Acciones del comentario de ${comment.user?.full_name || 'Usuario'} (${new Date(comment.created_at).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})`}
                            size="sm"
                            stopPropagation={false}
                          >
                            <Dropdown.Item onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditCommentText(comment.comment);
                            }}>
                              <i className="bi bi-pencil me-2"></i>Editar
                            </Dropdown.Item>
                            <Dropdown.Item className="text-danger" onClick={() => setDeleteCommentId(comment.id)}>
                              <i className="bi bi-trash me-2"></i>Eliminar
                            </Dropdown.Item>
                          </KebabMenu>
                        )}
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="mt-2">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                          />
                          <div className="d-flex gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => editCommentMutation.mutate({ commentId: comment.id, content: editCommentText })}
                              disabled={!editCommentText.trim() || editCommentMutation.isPending}
                            >
                              {editCommentMutation.isPending ? <Spinner size="sm" /> : 'Guardar'}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setEditingCommentId(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="comment-body mt-1">{comment.comment}</div>
                      )}
                    </div>
                  ))}
                  <div className="text-center mt-3">
                    <Button variant="outline-primary" size="sm" onClick={() => setShowCommentModal(true)}>
                      <i className="bi bi-plus-lg me-1"></i>Agregar comentario
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="checklist" title={`Checklist${checklistItems.length > 0 ? ` (${checklistItems.filter(i => i.is_completed).length}/${checklistItems.length})` : ''}`}>
          <Card>
            <Card.Body>
              {checklistItemsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              ) : (
                <>
                  {/* Progress bar */}
                  {checklistItems.length > 0 && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">
                          {checklistItems.filter(i => i.is_completed).length} de {checklistItems.length} completados
                        </small>
                        <small className="text-muted">
                          {checklistItems.length > 0
                            ? Math.round((checklistItems.filter(i => i.is_completed).length / checklistItems.length) * 100)
                            : 0}%
                        </small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div
                          className="progress-bar bg-success"
                          style={{
                            width: `${checklistItems.length > 0
                              ? (checklistItems.filter(i => i.is_completed).length / checklistItems.length) * 100
                              : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Checklist items */}
                  {checklistItems.length === 0 ? (
                    <div className="text-center py-3 text-muted">
                      <i className="bi bi-list-check display-4 mb-2 d-block"></i>
                      <p className="mb-2">No hay items en el checklist</p>
                      <small>Agrega items usando el campo de abajo</small>
                    </div>
                  ) : (
                    <div className="checklist-items-list mb-3">
                      {checklistItems.map((item) => (
                        <div
                          key={item.id}
                          className="d-flex align-items-start gap-2 py-2 border-bottom"
                        >
                          <Form.Check
                            type="checkbox"
                            checked={item.is_completed}
                            onChange={() => handleToggleChecklistItem(item.id, item.is_completed)}
                            disabled={checklistLoading}
                            className="mt-1"
                          />
                          <div className="flex-grow-1">
                            <span
                              style={{
                                textDecoration: item.is_completed ? 'line-through' : 'none',
                                color: item.is_completed ? '#6c757d' : 'inherit',
                              }}
                            >
                              {item.description}
                            </span>
                            {item.is_completed && item.completed_by_profile && (
                              <small className="d-block text-muted">
                                <i className="bi bi-check2 me-1"></i>
                                {item.completed_by_profile.full_name}
                                {item.completed_at && (
                                  <> - {new Date(item.completed_at).toLocaleDateString('es-CR', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}</>
                                )}
                              </small>
                            )}
                          </div>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => handleDeleteChecklistItem(item.id)}
                            disabled={checklistLoading}
                            title="Eliminar"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new item */}
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Agregar item al checklist..."
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChecklistItem();
                        }
                      }}
                      disabled={checklistLoading}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddChecklistItem}
                      disabled={checklistLoading || !newChecklistItem.trim()}
                      aria-label="Agregar item al checklist"
                    >
                      <i className="bi bi-plus-lg" aria-hidden="true"></i>
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Status Change Modal */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
        className="technician-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Estado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Estado actual: <Badge bg={statusConfig.bg}>{statusConfig.text}</Badge>
          </p>
          <div className="status-options">
            {availableStatuses.map((status) => {
              const config = getStatusConfig(status);
              return (
                <Button
                  key={status}
                  variant={`outline-${config.bg}`}
                  className="w-100 mb-2 status-option-btn"
                  onClick={() => handleStatusChange(status)}
                  disabled={statusMutation.isPending}
                >
                  <i className={`bi ${config.icon} me-2`}></i>
                  {config.text}
                </Button>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>

      {/* Comment Modal */}
      <Modal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        centered
        className="technician-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Agregar Comentario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Escribe tu comentario..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleAddComment}
            disabled={!commentText.trim() || commentMutation.isPending}
          >
            {commentMutation.isPending ? <Spinner size="sm" /> : 'Enviar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Comment Confirmation Modal */}
      <Modal
        show={!!deleteCommentId}
        onHide={() => setDeleteCommentId(null)}
        centered
        className="technician-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar Comentario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Estas seguro de que deseas eliminar este comentario? Esta accion no se puede deshacer.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteCommentId(null)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteCommentMutation.mutate(deleteCommentId)}
            disabled={deleteCommentMutation.isPending}
          >
            {deleteCommentMutation.isPending ? <Spinner size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Upload Modal */}
      <Modal
        show={showPhotoModal}
        onHide={handleClosePhotoModal}
        centered
        className="technician-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Subir Foto de Progreso</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {/* Hidden file inputs */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />

          {!photoPreview ? (
            <div className="photo-upload-area p-4">
              <i className="bi bi-camera display-4 text-muted mb-3 d-block"></i>
              <p className="text-muted mb-3">Selecciona o captura una foto</p>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <i className="bi bi-camera-fill me-2"></i>
                  Tomar Foto
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <i className="bi bi-image me-2"></i>
                  Seleccionar de Galeria
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <img
                src={photoPreview}
                alt="Vista previa"
                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', marginBottom: '1rem' }}
              />
              <Form.Control
                type="text"
                placeholder="Descripcion de la foto (opcional)"
                value={photoCaption}
                onChange={(e) => setPhotoCaption(e.target.value)}
                className="mb-3"
              />
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={handleUploadPhoto}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload me-2"></i>
                      Subir Foto
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSelectedFile(null);
                    setPhotoPreview(null);
                    setPhotoCaption('');
                    if (cameraInputRef.current) cameraInputRef.current.value = '';
                    if (galleryInputRef.current) galleryInputRef.current.value = '';
                  }}
                  disabled={uploadingPhoto}
                >
                  Cambiar Foto
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default TechnicianTaskDetail;
